import React, { useState, useEffect } from 'react';
import { TrackedAnchor } from '@components/analytics/TrackedLink';
import { motion } from 'framer-motion';
import { useTheme } from '@theme/useTheme';
import GradientBackground from '@components/ui/GradientBackground';
import { useAuth } from '@contexts/AuthContext';
import LoginModal from '@components/modals/LoginModal';
import ProfileModal from '@components/modals/ProfileModal';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const FAQ: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  // Global event listeners for modal controls
  useEffect(() => {
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "How accurate is the sleep apnea assessment?",
      answer: "Our assessment is based on established clinical guidelines and research. While it's not a replacement for professional medical diagnosis, it provides a reliable screening tool to identify potential risk factors. The assessment evaluates multiple factors including sleep patterns, medical history, and lifestyle factors to give you a comprehensive risk evaluation."
    },
    {
      question: "Is my personal information secure?",
      answer: "Yes, we take your privacy seriously. All data is encrypted and stored securely using industry-standard security measures. We never share your personal information with third parties without your explicit consent. Your health data is protected under HIPAA-compliant standards."
    },
    {
      question: "How long does the assessment take?",
      answer: "The assessment typically takes 10-15 minutes to complete. It includes questions about your sleep patterns, medical history, lifestyle factors, and symptoms. The questions are designed to be comprehensive yet easy to understand."
    },
    {
      question: "What should I do if the assessment indicates high risk?",
      answer: "If your assessment indicates a high risk for sleep apnea, we recommend consulting with a healthcare professional or sleep specialist for further evaluation. They may recommend a sleep study (polysomnography) to confirm the diagnosis and determine the best treatment options for your specific case."
    },
    {
      question: "Can I retake the assessment?",
      answer: "Yes, you can retake the assessment at any time. This can be helpful if your sleep patterns, health conditions, or lifestyle factors have changed. We recommend retaking the assessment annually or whenever you notice significant changes in your sleep quality."
    },
    {
      question: "What are the different types of sleep apnea?",
      answer: "There are three main types: Obstructive Sleep Apnea (OSA) - the most common form caused by blocked airways; Central Sleep Apnea - when the brain doesn't send proper signals to breathing muscles; and Complex Sleep Apnea - a combination of both obstructive and central sleep apnea."
    },
    {
      question: "What are the common symptoms of sleep apnea?",
      answer: "Common symptoms include loud snoring, episodes of stopped breathing during sleep, gasping for air, morning headaches, difficulty staying asleep, excessive daytime sleepiness, irritability, difficulty concentrating, and waking up with a dry mouth or sore throat."
    },
    {
      question: "Who is at risk for sleep apnea?",
      answer: "Risk factors include being overweight or obese, having a large neck circumference, being male, being over 40, having a family history of sleep apnea, smoking, alcohol use, nasal congestion, and certain medical conditions like diabetes, high blood pressure, and heart disease."
    },
    {
      question: "How is sleep apnea treated?",
      answer: "Treatment options include lifestyle changes (weight loss, avoiding alcohol), Continuous Positive Airway Pressure (CPAP) therapy, oral appliances, positional therapy, and in some cases, surgery. The best treatment depends on the severity and type of sleep apnea."
    },
    {
      question: "Can sleep apnea be cured?",
      answer: "While sleep apnea cannot always be completely cured, it can be effectively managed with proper treatment. Many people see significant improvement in their symptoms and quality of life with appropriate therapy. Lifestyle changes and consistent treatment can often lead to long-term management of the condition."
    },
    {
      question: "What happens if sleep apnea is left untreated?",
      answer: "Untreated sleep apnea can lead to serious health complications including high blood pressure, heart disease, stroke, diabetes, depression, and increased risk of accidents due to daytime sleepiness. It can also negatively impact your quality of life and relationships."
    },
    {
      question: "Is the assessment suitable for children?",
      answer: "Our current assessment is designed for adults (18+). Sleep apnea can also affect children, but the symptoms and risk factors are different. If you're concerned about a child's sleep patterns, please consult with a pediatrician or sleep specialist."
    }
  ];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      {/* Hero Section */}
      <div className="relative pt-32 pb-8 sm:pt-40 sm:pb-12 lg:pt-48 lg:pb-16 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
        {/* Gradient background */}
        <GradientBackground position="top" height="large" />

        <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                style={{ color: colors.heading }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                Frequently Asked Questions
              </motion.h1>
              <motion.p
                className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                style={{ color: colors.text }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                Find answers to common questions about sleep apnea and our assessment tool
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          {/* FAQ Section */}
          <section className="mt-4 mb-8 sm:mb-12">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="rounded-tr-2xl rounded-bl-2xl border overflow-hidden"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border
                  }}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 text-left flex justify-between items-center transition-all duration-300 hover:opacity-80"
                    style={{
                      backgroundColor: openFAQ === index ? colors.secondaryButton : 'transparent'
                    }}
                  >
                    <h3
                      className="text-lg sm:text-xl font-bold pr-4"
                      style={{ color: colors.heading }}
                    >
                      {faq.question}
                    </h3>
                    <div style={{ color: colors.primary }}>
                      {openFAQ === index ? (
                        <FaChevronUp className="text-xl" />
                      ) : (
                        <FaChevronDown className="text-xl" />
                      )}
                    </div>
                  </button>

                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p
                          className="text-base leading-relaxed"
                          style={{ color: colors.text }}
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>

          {/* Contact CTA */}
          <section className="text-center mb-8 sm:mb-12">
            <div
              className="p-8 lg:p-12 rounded-tr-2xl rounded-bl-2xl border"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border
              }}
            >
              <h2
                className="text-3xl sm:text-4xl font-bold mb-6"
                style={{ color: colors.heading }}
              >
                Still Have Questions?
              </h2>
              <p
                className="text-lg mb-8 max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                If you couldn't find the answer you're looking for, feel free to contact us directly. Our team is here to help with any questions about sleep apnea or our assessment tool.
              </p>
              <TrackedAnchor
                href="mailto:info@vitals7.com"
                className="px-8 py-4 rounded-full font-semibold text-white text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                style={{ backgroundColor: colors.primary }}
                gtmId="faq_contact_email"
                gtmSection="page_faq"
                gtmLabel="Contact Us"
              >
                Contact Us
              </TrackedAnchor>
            </div>
          </section>
        </div>
      </main>

      {/* Modals */}
      <LoginModal />

      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default FAQ;
